import { cloneObj } from 'helpers/AppHelpers';
import { handleDateFilter, handleIdsFilter } from 'helpers/ChipGenerator';

const defaultFilter = {
  filters: {
    searchQuery: '',
    priorityIds: [],
    severityIds: [],
    statusIds: [],
    openedFrom: null,
    openedTo: null,
    resolvedFrom: null,
    resolvedTo: null
  },
  pagination: {
    page: 1,
    totalPages: 1,
    limit: 10
  },
  sortRules: [],
  responseReceived: false
};

const generateInitialQuery = ({ tableParam, getTableLimit, isAllLimitEnabled, filter }) => {
  const defaultPage = defaultFilter.pagination.page;
  const defaultFilters = defaultFilter.filters;
  const defaultSortRules = defaultFilter.sortRules;

  const limit = getTableLimit(tableParam);
  const isAll = isAllLimitEnabled(tableParam);
  const pageFromFilter = filter?.pagination?.page;

  const page = limit === 100 && isAll ? defaultPage : pageFromFilter ?? defaultPage;

  return {
    filters: { ...defaultFilters, ...filter?.filters },
    pagination: { limit, page },
    sortRules: filter?.sortRules ?? defaultSortRules,
    responseReceived: false
  };
};

const OPENED_PREFIX = 'Opened';
const RESOLVED_PREFIX = 'Resolved';

const generateChips = (criteria, filters = {}) => {
  const { priorities, severities, statuses } = criteria;

  const { priorityIds, severityIds, statusIds, openedFrom, openedTo, resolvedFrom, resolvedTo } =
    filters;

  let newChips = [];

  const filterConfigs = [
    {
      ids: priorityIds,
      options: priorities,
      getLabel: (el) => el.displayName,
      key: 'priorityIds'
    },
    {
      ids: severityIds,
      options: severities,
      getLabel: (el) => el.displayName,
      key: 'severityIds'
    },
    {
      ids: statusIds,
      options: statuses,
      getLabel: (el) => el.displayName,
      key: 'statusIds'
    }
  ];

  filterConfigs.forEach((config) => {
    if (config?.ids?.length) {
      newChips = newChips.concat(
        handleIdsFilter(config.ids, config.options, config.getLabel, config.key)
      );
    }
  });

  if (openedFrom || openedTo) {
    newChips = newChips.concat(handleDateFilter(openedFrom, openedTo, OPENED_PREFIX));
  }

  if (resolvedFrom || resolvedTo) {
    newChips = newChips.concat(handleDateFilter(resolvedFrom, resolvedTo, RESOLVED_PREFIX));
  }

  return newChips.filter(Boolean);
};

const removeChip = (filters, deletedChip) => {
  const newFilters = cloneObj(filters);
  const { key, itemId } = deletedChip;

  switch (key) {
    case `duration_${OPENED_PREFIX}`:
      newFilters.openedTo = null;
      newFilters.openedFrom = null;
      break;
    case `dateFrom_${OPENED_PREFIX}`:
      newFilters.openedFrom = null;
      break;
    case `dateTo_${OPENED_PREFIX}`:
      newFilters.openedTo = null;
      break;
    case `duration_${RESOLVED_PREFIX}`:
      newFilters.resolvedTo = null;
      newFilters.resolvedFrom = null;
      break;
    case `dateFrom_${RESOLVED_PREFIX}`:
      newFilters.resolvedFrom = null;
      break;
    case `dateTo_${RESOLVED_PREFIX}`:
      newFilters.resolvedTo = null;
      break;
    default:
      newFilters[key] = newFilters[key].filter((id) => id !== itemId);
      break;
  }

  return newFilters;
};

const parseQuery = (query) => {
  const payload = {
    filters: { searchQuery: '' },
    pagination: { page: query.pagination.page, limit: query.pagination.limit }
  };

  const setFilters = (key, value) => {
    if (value?.length || value) {
      payload.filters[key] = value;
    }
  };

  const filterKeys = Object.keys(defaultFilter.filters);

  filterKeys.forEach((key) => setFilters(key, query.filters[key]));

  if (query.sortRules) {
    payload.sortRules = query.sortRules;
  }

  return payload;
};

const tableConfig = [
  { id: 1, label: 'ID', isSortable: true, parameter: 'id' },
  { id: 2, label: 'Job number', isSortable: true, parameter: 'jobNumber' },
  { id: 3, label: 'Job location', isSortable: true, parameter: 'jobLocation' },
  { id: 4, label: 'Status', isSortable: true, parameter: 'status' },
  { id: 5, label: 'Opened on', isSortable: true, parameter: 'openedOn' },
  { id: 6, label: 'Priority', isSortable: true, parameter: 'priority' },
  { id: 7, label: 'Severity', isSortable: true, parameter: 'severity' },
  { id: 8, label: 'Work Order Task Request', isSortable: false },
  { id: 9, label: 'Task Location', isSortable: true, parameter: 'taskLocation' },
  { id: 10, label: 'Requester name', isSortable: true, parameter: 'requesterName' },
  { id: 11, label: 'Resolved on', isSortable: true, parameter: 'resolvedOn' },
  { id: 12, label: 'Resolver name', isSortable: true, parameter: 'resolver' }
];

export { defaultFilter, generateInitialQuery, generateChips, removeChip, parseQuery, tableConfig };
